import { motion, AnimatePresence } from "framer-motion";
import { useContext } from "react";
import {
  LayoutDashboard,
  MessageSquare,
  Kanban,
  Users,
  BarChart3,
  Megaphone,
  Zap,
  Code,
  UserCog,
  FolderArchive,
  X,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

type Page = "dashboard" | "atendimentos" | "kanban" | "contacts";

interface SidebarProps {
  currentPage: Page;
  onPageChange: (page: Page) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const getMenuItems = () => [
  { icon: LayoutDashboard, label: i18n.t("v2.sidebar.dashboard"), page: "dashboard" as Page },
  { icon: MessageSquare, label: i18n.t("v2.sidebar.tickets"), page: "atendimentos" as Page },
  { icon: Kanban, label: i18n.t("v2.sidebar.kanban"), page: "kanban" as Page },
  { icon: Users, label: i18n.t("v2.sidebar.contacts"), page: "contacts" as Page },
  { icon: BarChart3, label: i18n.t("v2.sidebar.reports"), page: null },
  { icon: Megaphone, label: i18n.t("v2.sidebar.campaigns"), page: null },
  { icon: Zap, label: i18n.t("v2.sidebar.automation"), page: null },
  { icon: Code, label: i18n.t("v2.sidebar.api"), page: null },
  { icon: UserCog, label: i18n.t("v2.sidebar.users"), page: null },
  { icon: FolderArchive, label: i18n.t("v2.sidebar.files"), page: null },
];

export function Sidebar({ currentPage, onPageChange, isOpen = true, onClose }: SidebarProps) {
  const { user } = useContext(AuthContext) as any;
  const menuItems = getMenuItems();

  const handlePageChange = (page: Page) => {
    onPageChange(page);
    // Close sidebar on mobile after selecting a page
    if (onClose) {
      onClose();
    }
  };

  const sidebarContent = (
    <aside className="flex w-[260px] flex-shrink-0 flex-col bg-[var(--sidebar-bg)] border-r border-[var(--border-color)] py-4 sm:py-6 transition-colors duration-300 h-full">
      {/* Close button - Mobile Only */}
      {onClose && (
        <div className="flex lg:hidden justify-end px-4 mb-2">
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Menu Items */}
      <nav className="flex-1 px-3 sm:px-4 space-y-1 overflow-y-auto">
        {menuItems.map((item, index) => {
          const isActive = item.page === currentPage;
          const isClickable = item.page !== null;

          return (
            <motion.button
              key={item.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => isClickable && handlePageChange(item.page!)}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-[#2563EB] text-white shadow-lg shadow-blue-500/25"
                  : "text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]",
                !isClickable && "opacity-60"
              )}
            >
              <item.icon
                className={cn(
                  "h-5 w-5",
                  isActive ? "text-white" : "text-[var(--text-secondary)]"
                )}
              />
              <span>{item.label}</span>
            </motion.button>
          );
        })}
      </nav>

      {/* User Card */}
      <div className="mx-3 sm:mx-4 mt-4 rounded-xl bg-[var(--bg-tertiary)] p-3 sm:p-4 transition-colors duration-300">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={user?.profileImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "U")}&background=random`}
              alt={user?.name || "User"}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover"
            />
            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full bg-[#22C55E] ring-2 ring-[var(--sidebar-bg)]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
              {user?.name || "User"}
            </p>
            <p className="text-xs text-[var(--text-secondary)]">
              {user?.profile === "admin" ? i18n.t("v2.sidebar.administrator") : i18n.t("v2.sidebar.agent")}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );

  // Desktop: always show sidebar inline
  // Mobile: show as overlay when isOpen is true
  return (
    <>
      {/* Desktop Sidebar - always visible on lg+ */}
      <div className="hidden lg:flex">
        {sidebarContent}
      </div>

      {/* Mobile Sidebar - overlay */}
      <AnimatePresence>
        {isOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            {/* Sidebar Panel */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="absolute left-0 top-0 h-full"
            >
              {sidebarContent}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}